import { effect, Injectable } from '@angular/core';
import { getState, patchState } from '@ngrx/signals';
import { DataLayerType } from '../models/data-layer-type';
import { DataLayer } from '../models/data-layer';
import { InitializationHandler } from './initialization-handler';
import { RoutingService } from '../services/routing.service';
import { Indicator } from '../models/indicator';
import { BulkOperationService } from '../services/bulk-operation.service';

@Injectable({
  providedIn: 'root',
})
export class RoleBasedHandler extends InitializationHandler {
  initialized = false;
  constructor(
    routingService: RoutingService,
    private bulkOperationService: BulkOperationService
  ) {
    super(routingService);

    effect(() => {
      const indicatorState = getState(this.indicatorStore);
      const categoryState = getState(this.categoryStore);
      const dataLayerTypeState = getState(this.layerTypeStore);
      const geoDataState = getState(this.geoDataStore);
      const roleState = getState(this.roleStore);
      const workspaceState = getState(this.workspaceStore);
      setTimeout(() => {
        this.handleEffects(
          indicatorState,
          categoryState,
          dataLayerTypeState,
          geoDataState,
          roleState,
          workspaceState
        );
      }, 500);
    });
  }

  /**
   * Handles various application state triggers and executes corresponding actions,
   * such as updating stores, linking data, or performing bulk operations.
   *
   * @param indicatorState The current state and trigger details related to indicators.
   * @param categoryState The current state and trigger details related to categories.
   * @param dataLayerTypeState The state and trigger details related to data layer types.
   * @param geoDataState The state and trigger details associated with geographic data.
   * @param roleState The state and trigger details related to user roles.
   * @param workspaceState The state and trigger details pertinent to workspaces.
   * @return {void} This method does not return a value, as it primarily performs side effects.
   */
  handleEffects(
    indicatorState: any,
    categoryState: any,
    dataLayerTypeState: any,
    geoDataState: any,
    roleState: any,
    workspaceState: any
  ): void {
    if (
      (indicatorState.trigger &&
        indicatorState.trigger.indexOf('rebuildTree') > -1) ||
      (categoryState.trigger &&
        categoryState.trigger.indexOf('rebuildTree') > -1)
    ) {
      setTimeout(() => {
        this.globalStore.unsetTree();
      });
    }

    if (
      dataLayerTypeState.trigger &&
      dataLayerTypeState.trigger.includes('checkForInitialLayers')
    ) {
      this.layerStore.clearTrigger();
      this.addsInitialYears(
        dataLayerTypeState.triggerOptions,
        dataLayerTypeState.lastPersistedId
      );
    }

    if (
      geoDataState.trigger &&
      geoDataState.trigger.includes('linkDataLayer')
    ) {
      queueMicrotask(() => {
        this.linkNewGeoDataToLayer(geoDataState.triggerOptions);
      });
    }

    if (
      workspaceState.trigger &&
      workspaceState.trigger.includes('writeWorkspaceToEntities') &&
      workspaceState.triggerOptions
    ) {
      this.workspaceStore.clearTrigger();
      queueMicrotask(() => {
        this.bulkOperationService.addWorkspaceToEntities(
          workspaceState.lastPersistedId,
          workspaceState.triggerOptions['workspaceChildren']
        );
      });
    }

    if (
      categoryState.trigger &&
      categoryState.trigger.includes('removeIndicators')
    ) {
      const removedIds = categoryState.triggerOptions['removedIds'];

      if (removedIds && removedIds.length > 0) {
        const indicatorsToUpdate = { ...indicatorState.items };
        const asArray = Object.values(indicatorsToUpdate) as Indicator[];
        asArray.forEach((indicator: Indicator) => {
          if (removedIds.includes(indicator.category)) {
            delete indicatorsToUpdate[indicator._id];
          }
        });
        patchState(this.indicatorStore, { items: indicatorsToUpdate });
      }
    }

    if (
      indicatorState.trigger &&
      indicatorState.trigger.includes('removeChildren')
    ) {
      const removedIds = indicatorState.triggerOptions['removedIds'];

      if (removedIds && removedIds.length > 0) {
        const indicatorsToUpdate = { ...indicatorState.items };
        const asArray = Object.values(indicatorsToUpdate) as Indicator[];
        asArray.forEach((indicator: Indicator) => {
          if (removedIds.includes(indicator.parent)) {
            delete indicatorsToUpdate[indicator._id];
          }
        });
        patchState(this.indicatorStore, { items: indicatorsToUpdate });
      }
    }
  }

  /**
   * Sets the initial layer of the application based on the available data types and layers.
   * This method selects the first available layer of a specific type and updates the relevant stores.
   *
   * @return {void} Does not return a value.
   */
  setInitialLayer(): void {
    const types =
      this.layerTypeStore.polygonLayers() || this.layerTypeStore.asArray();
    const layersByType = this.layerStore.groupedByType();

    let type: DataLayerType | undefined;
    let layer: DataLayer | undefined;
    let idx = 0;

    while (layer === undefined && idx < this.layerTypeStore.count()) {
      type = types[idx];

      if (type && layersByType[type._id] && layersByType[type._id].length) {
        layer = this.layerStore.resolveDefaultByType(type, id =>
          this.indicatorDataStore.dataLayerHasData(id)
        );
      }
      idx++;
    }

    setTimeout(() => {
      if (type) this.globalStore.setSelectedType(type, true);
      if (layer) this.globalStore.setSelectedLayer(layer);
      this.permissionStore.setPermissions();
    });
  }

  /**
   * Adds initial years to the data layer store with specified modifications.
   *
   * @param {any} options - The trigger-options containing layers to persist
   * @param {string} dataLayerTypeId - The identifier for the type of data layer to be set as the parent for the initial years.
   * @param {string} type - The type of the parent datalayer type
   * @return {void} This method does not return a value.
   */
  addsInitialYears(options: any, dataLayerTypeId: string): void {
    const initialYears = options.initialYears;
    if (!initialYears) return;

    initialYears.forEach((dataLayer: DataLayer) => {
      dataLayer.type = options.type || 'Polygon';
      dataLayer.parent = dataLayerTypeId;
      dataLayer.published = false;
    });

    this.layerStore.insertMultiple({ items: initialYears });
  }

  /**
   * Links new geographical data to the corresponding layer(s) based on the provided options.
   *
   * @param {object} options - Configuration options for linking geo-data. Contains properties that determine the type of layer and specific parameters:
   * - `multipleYears` (boolean): Indicates if the operation involves multiple layers by year.
   * - `validationYears` (array): Array of years to validate against for filtering layers.
   * - `dataLayer` (object): A specific layer object to which geo-data should be linked.
   *
   * @return {void} This method does not return a value.
   */
  linkNewGeoDataToLayer(options: any): void {
    const layerType = this.globalStore.selectedType();
    if (!layerType) return; // warn...
    const typeId = layerType?._id;
    let layersToUpdate: DataLayer[] = [];
    if (options.singleLayerOnly && options.dataLayer) {
      // reacting to a Gebietsstandsänderung: only relink the edited layer,
      // siblings keep pointing to their previous geoData on purpose
      const layer = options.dataLayer;
      layer.geoData = this.geoDataStore.lastPersistedId();
      layersToUpdate = [layer];
    } else if (layerType.type === 'Polygon') {
      // all polygon-layer
      layersToUpdate = this.layerStore.filterByParent(typeId);
      layersToUpdate.map(layer => {
        layer.geoData = this.geoDataStore.lastPersistedId();
      });
    } else if (
      layerType.type === 'Point' &&
      options.multipleYears &&
      options.validationYears
    ) {
      // multiple poi-layer // todo: same geo-data each poi-layer - test new geo-upload
      layersToUpdate = this.layerStore.filterByParentAndYears(
        typeId,
        options.validationYears.map((year: string) => +year)
      );
      layersToUpdate.map(layer => {
        layer.geoData = this.geoDataStore.lastPersistedId();
      });
    } else if (options.dataLayers) {
      const layers: DataLayer[] = [...options.dataLayers];

      layersToUpdate = layers.map((layer: DataLayer) => {
        layer.geoData = this.geoDataStore.lastPersistedId();
        return layer;
      });
    } else if (options.dataLayer) {
      // single poi layer
      const layer = options.dataLayer;
      layer.geoData = this.geoDataStore.lastPersistedId();
      layersToUpdate = [layer];
    }

    if (!layersToUpdate || layersToUpdate.length === 0) return;

    this.layerStore.updateMultiple({
      items: layersToUpdate,
      message: { avoid: true },
    });
    this.globalStore.setSelectedGeoData(
      this.geoDataStore.items()[this.geoDataStore.lastPersistedId()]
    );
  }

  /**
   * Executes the initialization process for the application. Checks if necessary stores
   * are initialized, sets the initial layer, and starts a subscription for routing service.
   *
   * @return {void} Does not return a value.
   */
  performInitialization(): void {
    if (!this.initialized && this.areStoresInitialized()) {
      this.initialized = true;
      this.setInitialLayer();
      this.routingService.initiateRouteSubscription();
    }
  }
}
